// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import { useMutation, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { Prismic, PrismicText } from '@landsbankinn/prismic';
import {
  Button,
  Checkbox,
  Column,
  device,
  EmailInput,
  FormTitle,
  Input,
  KennitalaInput,
  NumberInput,
  PhoneInput,
  Row,
  Segment,
  theme,
  useEmailValidation,
  useKennitalaValidation,
  usePhoneValidation,
  typography,
  mobileGutterMargin,
} from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import keyBy from 'lodash.keyby';
import { rgba } from 'polished';
import React, { useContext, useState } from 'react';
import type { getEvent } from '../../graphql/EventRegistration/__generated__/getEvent';
import type {
  registerEvent,
  registerEventVariables,
} from '../../graphql/EventRegistration/__generated__/registerEvent';
import { getEvent as query } from '../../graphql/EventRegistration/eventRegistration';
import { registerEvent as mutation } from '../../graphql/EventRegistration/registerEvent';

const Host = styled.div``;

interface IProps {
  event_id: string;
  image: Prismic.Image;
  description: Prismic.RichText;
  use_kennitala: boolean;
  use_phonenumber: boolean;
  use_company_name: boolean;
  use_company_kennitala: boolean;
  max_number_registering: number;
  max_number_registering_label: string;
  fields: IFields[];
  title: string;
}
interface IFields {
  field_name: Prismic.RichText;
  field_type: string;
  field_position: string;
  field_size: string;
}

interface IFieldsValues extends IFields {
  value?: undefined | string;
}

const Title = styled.h2`
  ${typography.h2Big};
  margin-bottom: 20px;
`;

const Text = styled.div`
  margin-top: 20px;
  ${typography.body}
`;

const MarginRow = styled(Row)`
  margin-top: 140px;
  margin-bottom: 72px;
`;

const BigButton = styled(Button)`
  width: 100%;
`;

const CheckboxCenter = styled(Checkbox)`
  display: block;
  @media ${device.tablet} {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
`;
const FormSpacer = styled.div<{ smallTop?: boolean }>`
  height: 1px;
  background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
  ${(props) => (props.smallTop ? 'margin-top: 20px;' : 'margin-top: 40px;')};
  margin-bottom: 57px;
`;

const NoPaddingSegment = styled(Segment)`
  padding-top: 20px;
  padding-bottom: 0;
  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};

  ${mobileGutterMargin}
  @media ${device.tablet} {
    margin-left: initial;
    margin-right: initial;
  }
  &:last-of-type {
    padding-bottom: 57px;
  }
`;

export function EventRegistration({
  use_phonenumber,
  use_kennitala,
  use_company_name,
  use_company_kennitala,
  fields,
  title,
  description,
  max_number_registering,
  max_number_registering_label,
  event_id,
}: IProps) {
  const [submitted, setSubmitted] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailRef, isEmailValid, errorMessageEmail] = useEmailValidation(email);
  const [ssn, setSSN] = useState('');
  const [kennitalaRef, isKennitalaValid, errorMessageSSN] =
    useKennitalaValidation(ssn);
  const [phonenumber, setPhonenumber] = useState('');
  const [phoneRef, isPhoneValid, errorMessagePhone] =
    usePhoneValidation(phonenumber);
  const [companyName, setCompanyName] = useState('');
  const [companySSN, setCompanySSN] = useState('');
  const [companyKennitalaRef, isCompanyKennitalaValid, errorMessageCompanySSN] =
    useKennitalaValidation(companySSN);
  const [numberRegistrations, setMaxNumberRegistering] = useState(1);
  const [fieldValues, setFields] = useState<{ [key: string]: IFieldsValues }>(
    keyBy(fields, (i) => Prismic.text(i.field_name)),
  );

  const { client } = useContext(LandsbankinnContext);

  const { data, loading } = useQuery<getEvent>(query, {
    variables: {
      id:
        typeof window !== 'undefined' &&
        (window as any).btoa(`EventRegistration:${event_id}`),
    },
    client,
  });

  const [eventRegistration] = useMutation<
    registerEvent,
    registerEventVariables
  >(mutation, {
    client,

    onCompleted: () => {
      setSuccessText('Skráning á biðlista tókst!');
      setSubmitted(true);
    },
  });

  async function onFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const attributes = [];
    for (const key in fieldValues) {
      if (
        fieldValues.hasOwnProperty(key) &&
        fieldValues[key].value !== undefined
      ) {
        attributes.push({ label: key, value: fieldValues[key].value });
      }
    }
    eventRegistration({
      variables: {
        id: event_id,
        model: {
          name: name,
          email: email,
          kennitala: ssn,
          numberRegistering: numberRegistrations,
          companykennitala: companySSN,
          companyname: companyName,
          phonenumber: phonenumber,
          attributes: attributes,
        },
      },
    });
  }
  const formData =
    data?.node && data?.node.__typename === 'EventRegistration'
      ? data.node
      : undefined;

  if (loading || !formData) {
    return <div>Hleður..</div>;
  }

  return (
    <Host>
      <MarginRow>
        <Column
          lg={{ width: 6 / 12 }}
          md={{ width: 12 / 12 }}
          sm={{ width: 12 / 12 }}
        >
          <Title>{title}</Title>
        </Column>
        <Column
          lg={{ width: 6 / 12 }}
          md={{ width: 12 / 12 }}
          sm={{ width: 12 / 12 }}
        >
          <Text>
            <PrismicText value={description} />
          </Text>
        </Column>
      </MarginRow>

      {errorText && <div>{errorText}</div>}
      {successText !== '' && <div>{successText}</div>}
      {!submitted &&
        (formData.status === 'open' || formData.status === 'waitlist') && (
          <NoPaddingSegment>
            <form onSubmit={onFormSubmit}>
              <FormTitle
                text={
                  formData.status === 'waitlist'
                    ? 'Skráning á biðlista'
                    : 'Skráning'
                }
              />
              <Row>
                <Column
                  lg={{ width: 6 / 12 }}
                  md={{ width: 6 / 12 }}
                  sm={{ width: 12 / 12 }}
                  style={{ marginBottom: '32px' }}
                >
                  <Input
                    label="Nafn"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    inputSize="big"
                  />
                </Column>
                {use_phonenumber && (
                  <Column
                    lg={{ width: 3 / 12, offsetRight: 3 / 12 }}
                    md={{ width: 6 / 12 }}
                    sm={{ width: 12 / 12 }}
                    style={{ marginBottom: '32px' }}
                  >
                    <PhoneInput
                      label="Símanúmer"
                      value={phonenumber}
                      onChange={(e) => setPhonenumber(e.currentTarget.value)}
                      ref={phoneRef}
                      isValid={isPhoneValid}
                      errorMessage={errorMessagePhone}
                      inputSize="big"
                    />
                  </Column>
                )}
                <Column
                  lg={{ width: 6 / 12 }}
                  md={{ width: 6 / 12 }}
                  sm={{ width: 12 / 12 }}
                  style={{ marginBottom: '32px' }}
                >
                  <EmailInput
                    label="Netfang"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    ref={emailRef}
                    isValid={isEmailValid}
                    errorMessage={errorMessageEmail}
                    inputSize="big"
                  />
                </Column>

                {use_kennitala && (
                  <Column
                    lg={{ width: 6 / 12 }}
                    md={{ width: 6 / 12 }}
                    sm={{ width: 12 / 12 }}
                    style={{ marginBottom: '32px' }}
                  >
                    <KennitalaInput
                      label="Kennitala"
                      value={ssn}
                      onChange={(e) => setSSN(e.currentTarget.value)}
                      ref={kennitalaRef}
                      isValid={isKennitalaValid}
                      errorMessage={errorMessageSSN}
                      inputSize="big"
                    />
                  </Column>
                )}

                {fields &&
                  fields
                    .filter((x) => x.field_position === 'Person')
                    .map((label: IFields, key: number) => (
                      <>
                        {label.field_type === 'Text' && (
                          <Column
                            lg={
                              label.field_size === 'Big'
                                ? { width: 6 / 12 }
                                : label.field_size === 'Medium'
                                ? { width: 4 / 12 }
                                : { width: 12 / 12 }
                            }
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`person-${key}`}
                            style={{ marginBottom: '32px' }}
                          >
                            <Input
                              label={Prismic.text(label.field_name)}
                              value={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value
                              }
                              inputSize="big"
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e.currentTarget.value,
                                  },
                                })
                              }
                            />
                          </Column>
                        )}
                        {label.field_type === 'Boolean' && (
                          <Column
                            lg={{ width: 4 / 12 }}
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`person-${key}`}
                            style={{ marginBottom: '32px' }}
                          >
                            <Checkbox
                              checked={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value === 'true'
                                  ? true
                                  : false
                              }
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e ? 'true' : 'false',
                                  },
                                })
                              }
                              id={`event-registration-checkbox-person-${key}`}
                              label={Prismic.text(label.field_name)}
                            />
                          </Column>
                        )}
                      </>
                    ))}
              </Row>
              <FormSpacer />
              <Row>
                {use_company_name && (
                  <Column
                    lg={{ width: 6 / 12 }}
                    md={{ width: 6 / 12 }}
                    sm={{ width: 12 / 12 }}
                    style={{ marginBottom: '32px' }}
                  >
                    <Input
                      label="Nafn fyrirtækis"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.currentTarget.value)}
                      inputSize="big"
                    />
                  </Column>
                )}
                {use_company_kennitala && (
                  <Column
                    lg={{ width: 3 / 12 }}
                    md={{ width: 6 / 12 }}
                    sm={{ width: 12 / 12 }}
                  >
                    <KennitalaInput
                      label="Kennitala fyrirtækis"
                      type="text"
                      value={companySSN}
                      onChange={(e) => setCompanySSN(e.currentTarget.value)}
                      ref={companyKennitalaRef}
                      isValid={isCompanyKennitalaValid}
                      errorMessage={errorMessageCompanySSN}
                      inputSize="big"
                    />
                  </Column>
                )}
                {fields &&
                  fields
                    .filter((x) => x.field_position === 'Company')
                    .map((label: IFields, key: number) => (
                      <>
                        {label.field_type === 'Text' && (
                          <Column
                            lg={
                              label.field_size === 'Big'
                                ? { width: 6 / 12 }
                                : label.field_size === 'Medium'
                                ? { width: 4 / 12 }
                                : { width: 12 / 12 }
                            }
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`company-${key}`}
                            style={{ marginBottom: '32px' }}
                          >
                            <Input
                              label={Prismic.text(label.field_name)}
                              value={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value
                              }
                              inputSize="big"
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e.currentTarget.value,
                                  },
                                })
                              }
                            />
                          </Column>
                        )}
                        {label.field_type === 'Boolean' && (
                          <Column
                            lg={{ width: 3 / 12 }}
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`company-${key}`}
                          >
                            <CheckboxCenter
                              checked={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value === 'true'
                                  ? true
                                  : false
                              }
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e ? 'true' : 'false',
                                  },
                                })
                              }
                              id={`event-registration-checkbox-company-${key}`}
                              label={Prismic.text(label.field_name)}
                            />
                          </Column>
                        )}
                      </>
                    ))}
              </Row>
              <FormSpacer />
              <Row>
                {fields &&
                  fields
                    .filter((x) => x.field_position === 'Spouse')
                    .map((label: IFields, key: number) => (
                      <>
                        {label.field_type === 'Text' && (
                          <Column
                            lg={
                              label.field_size === 'Big'
                                ? { width: 6 / 12 }
                                : label.field_size === 'Medium'
                                ? { width: 4 / 12 }
                                : { width: 12 / 12 }
                            }
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`spouse-${key}`}
                            style={{ marginBottom: '32px' }}
                          >
                            <Input
                              label={Prismic.text(label.field_name)}
                              value={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value
                              }
                              inputSize="big"
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e.currentTarget.value,
                                  },
                                })
                              }
                            />
                          </Column>
                        )}
                        {label.field_type === 'Boolean' && (
                          <Column
                            lg={{ width: 3 / 12 }}
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`spouse-${key}`}
                          >
                            <CheckboxCenter
                              checked={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value === 'true'
                                  ? true
                                  : false
                              }
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e ? 'true' : 'false',
                                  },
                                })
                              }
                              id={`event-registration-checkbox-spouse-${key}`}
                              label={Prismic.text(label.field_name)}
                            />
                          </Column>
                        )}
                      </>
                    ))}
              </Row>
              <FormSpacer />
              <Row>
                {fields &&
                  fields
                    .filter((x) => x.field_position === 'Other')
                    .map((label: IFields, key: number) => {
                      <>
                        {label.field_type === 'Text' && (
                          <Column key={`other-${key}`}>
                            <Input
                              label={Prismic.text(label.field_name)}
                              value={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value
                              }
                              inputSize="big"
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e.currentTarget.value,
                                  },
                                })
                              }
                            />
                          </Column>
                        )}
                        {label.field_type === 'Boolean' && (
                          <Column
                            lg={{ width: 3 / 12 }}
                            md={{ width: 6 / 12 }}
                            sm={{ width: 12 / 12 }}
                            key={`other-${key}`}
                          >
                            <Checkbox
                              checked={
                                fieldValues[Prismic.text(label.field_name)]
                                  .value === 'true'
                                  ? true
                                  : false
                              }
                              onChange={(e) =>
                                setFields({
                                  ...fieldValues,
                                  [Prismic.text(label.field_name)]: {
                                    ...fieldValues[
                                      Prismic.text(label.field_name)
                                    ],
                                    value: e ? 'true' : 'false',
                                  },
                                })
                              }
                              id={`event-registration-checkbox-other-${key}`}
                              label={Prismic.text(label.field_name)}
                            />
                          </Column>
                        )}
                      </>;
                    })}
                {formData.maxNumberOfRegistrations && (
                  <Column
                    lg={{ width: 6 / 12 }}
                    md={{ width: 6 / 12 }}
                    sm={{ width: 12 / 12 }}
                  >
                    <NumberInput
                      id="max_number"
                      label={
                        max_number_registering_label !== null
                          ? max_number_registering_label
                          : `Fjöldi miða (hámark ${max_number_registering})`
                      }
                      value={numberRegistrations}
                      onChange={(val) => {
                        if (val < max_number_registering) {
                          setMaxNumberRegistering(val);
                        }
                      }}
                    />
                  </Column>
                )}
              </Row>
              <FormSpacer />
              <Row>
                <Column
                  lg={{ width: 6 / 12, offset: 6 / 12 }}
                  md={{ width: 6 / 12 }}
                  sm={{ width: 12 / 12 }}
                >
                  <BigButton
                    bg={theme.colors.primary}
                    title="Senda"
                    type="submit"
                  />
                </Column>
              </Row>
            </form>
          </NoPaddingSegment>
        )}
      {!submitted && formData.status === 'full' && (
        <div>Því miður er fullt á þennan viðburð.</div>
      )}
      {!submitted && formData.status === 'closed' && (
        <div>Skráningarfrestur fyrir þennan viðburð er útrunninn.</div>
      )}
    </Host>
  );
}
